// =============================================================================
//  pageHtmlDocument.js — HTML/CSS/JS "lens" helpers for the </> Edit HTML
//  Code modal (PageHtmlSourceModal.jsx).
//
//  PageBuilder's `htmlSource.html` (server_elakay/elakay_models/
//  pageBuilderModel.js) is -- and stays -- a SINGLE complete HTML document
//  string, exactly like the read-only "Code HTML généré" panel already built
//  from buildSiteHtml() in PageBuilder.jsx. There is no per-field HTML/CSS/JS
//  storage to split into (unlike howWeDoIt's htmlCode/customCss/customJs,
//  which are three independent DB fields -- see howWeDoItPageDocument.js).
//
//  So, deliberately, this file does NOT attempt a generic/destructive
//  HTML<->CSS<->JS parser. "HTML" IS the one full document (the same string
//  PageHtmlSourceModal has always edited) -- CSS/JS are only ever a
//  CONVENIENCE lens on top of it, following the exact same safety contract
//  howWeDoItPageDocument.js's getFullPageCssSection/JsSection use: a marker
//  this module itself authors (a `data-elakay-pagebuilder-custom-*`
//  attribute), never a generic guess at "the" style/script tag. If a document
//  doesn't carry that marker yet (e.g. it was just seeded from buildSiteHtml,
//  or hand-pasted from elsewhere), the getter simply returns "" and the
//  setter safely INSERTS a new, clearly-marked block rather than overwriting
//  unrelated <style>/<script> tags it can't safely identify. Nothing already
//  present in the "HTML" tab is ever silently dropped or rewritten by editing
//  the CSS/JS tabs.
// =============================================================================

const CSS_MARKER = "data-elakay-pagebuilder-custom-css";
const JS_MARKER = "data-elakay-pagebuilder-custom-js";
export const PAGE_BUILDER_HTML_LIMIT = 500000;

const CSS_SECTION_RE = new RegExp(`(<style[^>]*${CSS_MARKER}[^>]*>)([\\s\\S]*?)(</style>)`, "i");
const JS_SECTION_RE = new RegExp(`(<script[^>]*${JS_MARKER}[^>]*>)([\\s\\S]*?)(</script>)`, "i");

export const getHtmlCssSection = (html = "") => (CSS_SECTION_RE.exec(html)?.[2] ?? "").trim();
export const getHtmlJsSection = (html = "") => (JS_SECTION_RE.exec(html)?.[2] ?? "").trim();

const BODY_SECTION_RE = /(<body\b[^>]*>)([\s\S]*?)(<\/body>)/i;

// The HTML tab edits only the body markup. The managed JavaScript block is
// hidden from that lens and reattached by the setter, so moving between HTML
// and JavaScript never duplicates or silently deletes custom code.
export const getHtmlBodySection = (html = "") => {
  const body = BODY_SECTION_RE.exec(html)?.[2];
  if (body != null) return String(body).replace(JS_SECTION_RE, "").trim();
  const headEnd = String(html).match(/<\/head\s*>/i);
  const editable = headEnd
    ? String(html).slice((headEnd.index || 0) + headEnd[0].length).replace(/<\/html\s*>\s*$/i, "")
    : html;
  return String(editable).replace(JS_SECTION_RE, "").trim();
};

export const setHtmlBodySection = (html = "", body = "") => {
  const script = JS_SECTION_RE.exec(html)?.[0] || "";
  const nextBody = `${body.trim()}${script ? `\n${script}` : ""}`;
  if (BODY_SECTION_RE.test(html)) {
    return html.replace(BODY_SECTION_RE, (_match, open, _old, close) => `${open}\n${nextBody}\n${close}`);
  }
  if (/<\/head\s*>/i.test(html)) {
    const prefix = html.slice(0, html.search(/<\/head\s*>/i)).concat(html.match(/<\/head\s*>/i)?.[0] || "</head>");
    const htmlClose = /<\/html\s*>/i.test(html) ? "\n</html>" : "";
    return `${prefix}\n<body>\n${nextBody}\n</body>${htmlClose}`;
  }
  return `<!DOCTYPE html>\n<html>\n<head>\n<meta charset="UTF-8">\n</head>\n<body>\n${nextBody}\n</body>\n</html>`;
};

const spliceOrInsert = (html, regex, marker, tag, value, insertBeforeRe, fallbackAppend) => {
  if (regex.test(html)) {
    return html.replace(regex, (_match, open, _old, close) => `${open}${value}${close}`);
  }
  if (!value.trim()) return html; // nothing to add, nothing to remove -- safe no-op
  const block = `<${tag} ${marker}>\n${value}\n</${tag}>\n`;
  if (insertBeforeRe.test(html)) return html.replace(insertBeforeRe, (match) => `${block}${match}`);
  return `${html}\n${block}${fallbackAppend || ""}`;
};

export const setHtmlCssSection = (html = "", css = "") =>
  spliceOrInsert(html, CSS_SECTION_RE, CSS_MARKER, "style", css, /<\/head>/i);

export const setHtmlJsSection = (html = "", js = "") =>
  spliceOrInsert(html, JS_SECTION_RE, JS_MARKER, "script", js, /<\/body>/i);

// Minimal, deliberately conservative CSS reindenter -- same algorithm as
// howWeDoIt/HowWeDoItCodeEditorModal.jsx's own formatCssText(), reused here
// rather than imported (that one is module-private) since it's a tiny, pure,
// dependency-free function. No formatter is bundled for HTML/JS in either
// editor -- skipping reformatting there is deliberate, not an omission (a
// generic HTML/JS beautifier risks mangling attributes/inline scripts it
// doesn't fully understand; CSS's `{`/`}`/`;` grammar is simple enough to
// reindent safely with a plain regex pass).
export const formatCssText = (css = "") => {
  let depth = 0;
  return css
    .replace(/\s*\{\s*/g, " {\n")
    .replace(/\s*\}\s*/g, "\n}\n")
    .replace(/;(?!\s*\n)/g, ";\n")
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      if (line === "}") depth = Math.max(0, depth - 1);
      const indented = "  ".repeat(depth) + line;
      if (line.endsWith("{")) depth += 1;
      return indented;
    })
    .join("\n");
};

export const byteLength = (value = "") => {
  try { return new TextEncoder().encode(String(value)).length; } catch (_error) { return unescape(encodeURIComponent(String(value))).length; }
};

export const analyzePageHtml = (html = "") => {
  const source = String(html || "");
  const errors = [];
  const warnings = [];
  const chars = source.length;
  if (!source.trim()) errors.push({ code: "EMPTY", message: "The HTML code cannot be empty." });
  if (chars > PAGE_BUILDER_HTML_LIMIT) {
    errors.push({ code: "TOO_LARGE", message: `HTML exceeds the ${PAGE_BUILDER_HTML_LIMIT.toLocaleString()} character limit.` });
  }
  const cssMarkers = (source.match(new RegExp(CSS_MARKER, "gi")) || []).length;
  const jsMarkers = (source.match(new RegExp(JS_MARKER, "gi")) || []).length;
  if (cssMarkers > 1) errors.push({ code: "DUPLICATE_CSS", message: "More than one managed CSS section was found." });
  if (jsMarkers > 1) errors.push({ code: "DUPLICATE_JS", message: "More than one managed JavaScript section was found." });
  const externalScripts = (source.match(/<script\b[^>]*\bsrc\s*=/gi) || []).length;
  const inlineHandlers = (source.match(/\son[a-z]+\s*=/gi) || []).length;
  const forms = (source.match(/<form\b/gi) || []).length;
  if (externalScripts) warnings.push({ code: "EXTERNAL_SCRIPT", message: `${externalScripts} external script(s) will be blocked.` });
  if (inlineHandlers) warnings.push({ code: "INLINE_HANDLER", message: `${inlineHandlers} inline event handler(s) will be removed; use the JavaScript tab instead.` });
  if (forms) warnings.push({ code: "FORM", message: `${forms} form(s) will remain isolated and cannot submit.` });
  if (!/<html\b/i.test(source) || !/<body\b/i.test(source)) {
    warnings.push({ code: "FRAGMENT", message: "This is an HTML fragment; it will be wrapped as a complete document when editing the HTML tab." });
  }
  return {
    valid: errors.length === 0,
    errors,
    warnings,
    chars,
    bytes: byteLength(source),
    scriptCount: (source.match(/<script\b/gi) || []).length,
  };
};

// Runs INSIDE the sandboxed preview iframe only -- forwards console.*/runtime
// errors to the parent via postMessage so the modal's Console tab can show
// them. Same technique, reused verbatim in spirit, as
// howWeDoIt/HowWeDoItSandboxedFrame.jsx's own CONSOLE_SHIM, with its own
// `source` string so the two sandboxes (different pages) never cross-talk.
export const PAGE_BUILDER_SANDBOX_SOURCE = "elakay-pagebuilder-html-sandbox";

export const PAGE_BUILDER_CONSOLE_SHIM = `<script>(function(){
function post(kind,args){try{parent.postMessage({source:"${PAGE_BUILDER_SANDBOX_SOURCE}",kind:kind,args:args.map(function(a){try{return typeof a==="string"?a:JSON.stringify(a);}catch(e){return String(a);}})},"*");}catch(e){}}
["log","warn","error","info"].forEach(function(method){var original=console[method];console[method]=function(){post(method,Array.prototype.slice.call(arguments));if(original)original.apply(console,arguments);};});
window.addEventListener("error",function(e){post("error",[e.message+" ("+e.filename+":"+e.lineno+")"]);});
window.addEventListener("unhandledrejection",function(e){post("error",["Unhandled promise rejection: "+(e.reason&&e.reason.message||e.reason)]);});
})();</script>`;

// Injects the console shim right after <body ...> (or, lacking one, right at
// the very start) so it is present as early as possible without ever
// touching the author's own markup. Never executed outside the sandboxed
// iframe (see PageHtmlSourceModal.jsx's `sandbox="allow-scripts"`, no
// allow-same-origin).
export const withConsoleShim = (html = "") => {
  if (/<body[^>]*>/i.test(html)) {
    return html.replace(/<body([^>]*)>/i, (match) => `${match}\n${PAGE_BUILDER_CONSOLE_SHIM}\n`);
  }
  return `${PAGE_BUILDER_CONSOLE_SHIM}\n${html}`;
};
